import { FC, ReactNode, useState } from 'react';

import { Box, Container, rem, ToastContainerSyle } from '@bonusly/design-system';
import { useFeatureFlag, Variant } from '@bonusly/who-am-i';

import { UserNavbar } from '../../modules/user-navbar/components/user-navbar';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { Reveal } from '../display/reveal';
import { useSpoofing } from './hooks/useSpoofing';

export interface AppLayoutProps {
	navbar: ReactNode;
	children: ReactNode;
}

export const AppLayout: FC<AppLayoutProps> = ({ children, navbar }) => {
	const { data } = useSpoofing();
	const isHomepageRedesignEnabled = useFeatureFlag(Variant.HomepageRedesign);
	const [isExpanded, setIsExpanded] = useState(true);
	const toggleSidebar = () => setIsExpanded(!isExpanded);

	if (isHomepageRedesignEnabled) {
		const isSpoofing = data?.is_spoofing && !data?.is_demoing;
		const spoofingBannerHeight = rem(60);

		return (
			<Box bg="bg.subtle" minH="100vh">
				<Reveal if={isSpoofing}>
					<SpoofingWarning />
				</Reveal>
				<ToastContainerSyle style={{ 'padding-top': '0px' }} />

				<Box display="flex" minH={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}>
					{/* USER NAVIGATION */}
					<Box
						position="fixed"
						left="0"
						top={isSpoofing ? spoofingBannerHeight : '0'}
						height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
						width={isExpanded ? rem(260) : rem(56)}
						bg="bg.canvas"
						zIndex="docked"
						transition={'all 0.3s cubic-bezier(0.2, 0, 0, 1)'}
						overflow="hidden"
					>
						{/* TODO: handle rendering shell for NavFallback */}
						<UserNavbar isExpanded={isExpanded} toggleSidebar={toggleSidebar} />
					</Box>

					{/* CONTENTS */}
					<Box ml={isExpanded ? rem(260) : rem(56)} flex="1" minW="0" transition={'margin-left 0.3s cubic-bezier(0.2, 0, 0, 1)'}>
						{children}
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box bg="bg.subtle" display="flex" flexDirection="column" minH="100vh">
			<Box
				backdropFilter="blur(20px)"
				bg="bg.topNav"
				position="sticky"
				top="0"
				zIndex="docked"
				w="100%"
				id="nav-wrapper"
				borderBottomColor="border.default"
				borderBottomWidth="1px"
				borderBottomStyle="solid"
			>
				{data?.is_spoofing && !data?.is_demoing && <SpoofingWarning />}

				<ToastContainerSyle style={{ 'padding-top': rem(64) }} />

				<Container maxW={'100%'} px={0}>
					{navbar}
				</Container>
			</Box>

			<Box display="flex" flexDirection="column" flexGrow={1} pos="relative" zIndex="base">
				{children}
			</Box>
		</Box>
	);
};
